import { Product } from "~/types/Data/product/Product";
import { WishListResponse } from "~/types/Data/product/WishList";
import { useApi } from "~/composables/Common/useApi";
import useTrackEvents from "~/composables/Common/useTrackEvents";
import { BaseResponse } from "~/types/Data/Common/APIs";

export default function useWishlist() {
  const wishList = useState<Product[] | undefined>("wishlist", () => undefined);
  const loadingProductId = useState<string>(
    "wishlist-loading-product-id",
    () => "",
  );

  const userToken = useCookie("hs-token");
  const nuxtApp = useNuxtApp();
  const { addToast } = useToast();
  const { trackAddToWishlist } = useTrackEvents();

  const initWishlist = async (baseUrl?: string, value?: string) => {
    if (!!nuxtApp.$features?.wishlist && userToken.value) {
      const { res } = await useApi<WishListResponse>(
        `/api/v2/wishlists?sort=${value ?? "recent"}`,
        {
          authToken: userToken.value,
        },
        {
          baseUrl,
        },
      );

      wishList.value = res?.data;
    }
  };

  const addToWishlist = async (productID: string) => {
    loadingProductId.value = productID;

    const { res } = await useApi<BaseResponse<Product>>(
      "/api/v2/wishlists",
      {
        method: "POST",
        authToken: userToken.value,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: productID,
        }),
      },
      {
        catchError: true,
      },
    );

    if (res) {
      addToast("Added to wishlist", "success");
      wishList.value?.push(res.data);
      trackAddToWishlist(productID);
    } else {
      addToast("Failed to add to wishlist", "error");
    }

    loadingProductId.value = "";
  };

  const removeFromWishlist = async (productID: string) => {
    loadingProductId.value = productID;
    const { err } = await useApi(
      `/api/v2/wishlists/${productID}`,
      {
        method: "DELETE",
        authToken: userToken.value,
      },
      {
        catchError: true,
      },
    );

    if (err) {
      addToast("Failed to remove from wishlist", "error");
    } else {
      addToast("Removed from wishlist", "success");
      wishList.value = wishList.value?.filter(
        (product) => product.id !== productID,
      );
    }

    loadingProductId.value = "";
  };

  const inWishlist = (productID: string) => {
    return !!wishList.value?.some((product) => product.id === productID);
  };

  return {
    wishList,
    loadingProductId,
    inWishlist,
    initWishlist,
    addToWishlist,
    removeFromWishlist,
  };
}
